/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type FC, useCallback, useEffect, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import Image from '@atlaskit/image';
import { Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useFireAnalyticsEvent } from '../../analytics/use-fire-analytics';
import type { BlankObjectContentType } from '../../types';

import writeIllustrationDark from './assets/write-illustration-dark.svg';
import writeIllustrationLight from './assets/write-illustration-light.svg';

const i18n = defineMessages({
	title: {
		id: 'conversation-assistant-blank-object-experience.rovo-templates-callout.title',
		defaultMessage: "Can't find what you're looking for?",
		description: 'Title for the Rovo promotional callout frame',
	},
	description: {
		id: 'conversation-assistant-blank-object-experience.rovo-templates-callout.description',
		defaultMessage: 'Get help structuring and accelerating your work with Rovo.',
		description: 'Description text for the Rovo promotional callout frame',
	},
	createWithRovo: {
		id: 'conversation-assistant-blank-object-experience.rovo-templates-callout.create-with-rovo',
		defaultMessage: 'Create with Rovo',
		description: 'Label for the Create with Rovo button',
	},
});

/**
 * Display mode configuration for RovoTemplatesCallout.
 * - 'compact': standard layout with 64px illustration and space.200 padding/gap
 * - 'wide': larger layout with 88px illustration and space.250 padding/gap
 */
const displayConfig = {
	compact: {
		illustrationSize: 64,
		gap: 'space.200',
	},
	wide: {
		illustrationSize: 88,
		gap: 'space.250',
	},
} as const;

export type RovoTemplatesCalloutDisplay = keyof typeof displayConfig;

const styles = cssMap({
	frame: {
		boxSizing: 'border-box',
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		marginBlock: token('space.250'),
		alignSelf: 'stretch',
	},
	spacingCompact: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	spacingWide: {
		paddingTop: token('space.250'),
		paddingRight: token('space.250'),
		paddingBottom: token('space.250'),
		paddingLeft: token('space.250'),
	},
	illustration: {
		flexShrink: 0,
	},
});

export interface RovoTemplatesCalloutProps {
	onCreateWithRovo?: () => void;
	/** The current content type being created */
	contentType?: BlankObjectContentType;
	/**
	 * Controls the display mode of the callout.
	 * - `'compact'` (default): 64px illustration, space.200 padding and gap — for standard contexts such as page templates
	 * - `'wide'`: 88px illustration, space.250 padding and gap — for larger contexts such as the whiteboards templates modal
	 */
	display?: RovoTemplatesCalloutDisplay;
}

export const RovoTemplatesCallout: FC<RovoTemplatesCalloutProps> = ({
	onCreateWithRovo,
	contentType,
	display = 'compact',
}) => {
	const intl = useIntl();
	const { fireAnalyticsEvent } = useFireAnalyticsEvent();
	const hasScreenFired = useRef(false);

	// Fire screen event on first render
	useEffect(() => {
		if (!hasScreenFired.current) {
			hasScreenFired.current = true;
			fireAnalyticsEvent({
				eventType: 'screen',
				name: 'rovoTemplatesCallout',
				attributes: {
					contentType,
				},
			});
		}
	}, [fireAnalyticsEvent, contentType]);

	const handleCreateWithRovoClick = useCallback(() => {
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createWithRovoButton',
			attributes: {
				contentType,
			},
		});
		onCreateWithRovo?.();
	}, [onCreateWithRovo, fireAnalyticsEvent, contentType]);

	const config = displayConfig[display];

	return (
		<Flex
			xcss={cx(styles.frame, display === 'wide' ? styles.spacingWide : styles.spacingCompact)}
			justifyContent="center"
			alignItems="center"
			gap={config.gap}
		>
			<div css={styles.illustration}>
				<Image
					src={writeIllustrationLight}
					srcDark={writeIllustrationDark}
					alt=""
					width={config.illustrationSize}
					height={config.illustrationSize}
				/>
			</div>

			<Stack grow="fill" space="space.100">
				<Heading size="xsmall">{intl.formatMessage(i18n.title)}</Heading>
				<Text>{intl.formatMessage(i18n.description)}</Text>

				<Inline space="space.050" shouldWrap>
					<Button iconBefore={AiGenerativeTextIcon} onClick={handleCreateWithRovoClick}>
						{intl.formatMessage(i18n.createWithRovo)}
					</Button>
				</Inline>
			</Stack>
		</Flex>
	);
};
